/**
 * Read-only calendar hydration followed by the exact local ghost preview. The server supplies
 * authorized inputs, capacity and calendar records; the separate browser Worker supplies only
 * tentative suggestions. No refresh invokes server rollover or falls back to server search.
 * @module apps/web/src/lib/browserGhostBlocks
 */
import type { GhostBlocksData } from '@fluxability/types';
import type { ApiResponse } from './api';
import { previewBrowserGhosts, warmBrowserGhosts } from './browserScheduling';
import { getSchedulingSessionEpoch } from './browserScheduleFlow';
import { captureTaskSnapshot, isTaskSnapshotCurrent } from '@/stores/tasksStore';
import { useUserStore } from '@/stores/userStore';
import { useScheduleStore } from '@/stores/scheduleStore';
import { toast } from '@/hooks/use-toast';

/** Capture session, logout intent and visible date before grouped calendar reads; call again immediately before applying their projections. */
export function captureCalendarReadFence(): () => boolean {
  const owner = useUserStore.getState().userId;
  const token = localStorage.getItem('flexiplanner-session-token');
  const epoch = getSchedulingSessionEpoch();
  const date = useScheduleStore.getState().currentDate.getTime();
  return () =>
    Boolean(owner) &&
    owner === useUserStore.getState().userId &&
    token === localStorage.getItem('flexiplanner-session-token') &&
    epoch === getSchedulingSessionEpoch() &&
    date === useScheduleStore.getState().currentDate.getTime();
}

/**
 * Preserve the legacy calendar response while replacing its empty suggestions with local output.
 * Capture ownership and local revision before the HTTP read, recheck before compute and delivery,
 * and keep authorized snapshots out of the returned presentation data. Infeasible capacity never
 * starts a Worker. Transport/runtime failures remain explicit failed responses with no optimizer fallback.
 */
export async function readBrowserGhostBlocks(
  read: () => Promise<ApiResponse<GhostBlocksData>>
): Promise<ApiResponse<GhostBlocksData>> {
  const currentOwner = captureCalendarReadFence();
  const snapshot = captureTaskSnapshot();
  /** A changed task projection invalidates suggestions without changing any canonical records. */
  const fence = () => {
    if (!currentOwner()) throw new Error('Your scheduling session changed.');
    if (!isTaskSnapshotCurrent(snapshot))
      throw new Error('Activities changed while preparing suggestions. Refresh the calendar.');
  };
  try {
    fence();
    // Worker start-up (a code-only warm-up, no user data) overlaps the authorized read.
    warmBrowserGhosts();
    const response = await read();
    fence();
    if (!response.success || !response.data) return response;
    const { browser_snapshot: authorized, ...presentation } = response.data;
    // A read-only server response can omit a snapshot when no feasible suggestions exist.
    if (presentation.capacity?.is_feasible !== true)
      return { ...response, data: { ...presentation, ghost_blocks: [] } };
    if (!authorized || typeof presentation.week_start !== 'string')
      throw new Error('Local calendar suggestions are unavailable. Reload to update Fluxability.');
    const ghosts = await previewBrowserGhosts(authorized, presentation.week_start);
    fence();
    return { ...response, data: { ...presentation, ghost_blocks: ghosts.blocks } };
  } catch (error) {
    const message =
      error instanceof Error ? error.message : 'Local calendar suggestions are unavailable.';
    if (currentOwner() && isTaskSnapshotCurrent(snapshot))
      toast({
        title: 'Calendar suggestions unavailable',
        description: message,
        variant: 'destructive',
      });
    return { success: false, error: { code: 'BROWSER_GHOST_PREVIEW_FAILED', message } };
  }
}
